import { centsToDollars, parsePrice } from "helpers/price";

export type ServiceRow = {
  cptCode?: string;
  code?: string;
  quantity?: number;
  amount?: number;
};

export function summariseServices(rows: ServiceRow[]) {
  const byCptCode = new Map<
    string,
    { quantity: number; amountInCents: number }
  >();

  rows.forEach((row) => {
    const key = row.cptCode ?? row.code ?? "";
    const entry = byCptCode.get(key);

    if (entry) {
      entry.amountInCents += row.amount || 0;
      return;
    }

    byCptCode.set(key, {
      quantity: row.quantity || 0,
      amountInCents: row.amount || 0,
    });
  });

  const groups = [...byCptCode.values()];

  return {
    services: groups.length,
    units: groups.reduce((sum, group) => sum + group.quantity, 0),
    total: centsToDollars(
      groups.reduce(
        (sum, group) => sum + group.amountInCents * group.quantity,
        0
      )
    ),
  };
}

const plural = (count: number, word: string) =>
  `${count} ${count === 1 ? word : `${word}s`}`;

export function ServicesFooter({ rows }: { rows: ServiceRow[] }) {
  const { services, units, total } = summariseServices(rows);
  const hasUnits = rows.some((row) => row.quantity != null);
  const hasAmounts = rows.some((row) => row.amount != null);

  return (
    <div
      data-testid="servicesFooter"
      className="flex flex-wrap align-items-center justify-content-between gap-2"
      style={{
        padding: "0.75rem 1rem",
        background: "var(--brand-gray-50)",
        borderTop: "1px solid var(--brand-gray-200)",
      }}
    >
      <span className="text-sm" style={{ color: "var(--brand-gray-600)" }}>
        {plural(services, "service")}
        {hasUnits ? ` · ${plural(units, "unit")}` : ""}
      </span>

      {hasAmounts && (
        <span
          className="text-base font-bold"
          style={{ color: "var(--brand-black)" }}
        >
          {parsePrice(total)}
        </span>
      )}
    </div>
  );
}
